/**
 * Formats a North American phone number as `123-123-1234`. Ten digits, or
 * eleven starting with the country code 1, are reformatted; anything else
 * (international, extensions, partial numbers) comes back trimmed but
 * otherwise untouched, so a foreign number is never mangled. Returns '' for
 * an empty value.
 */
export function formatPhone(raw: string | number | null | undefined): string {
  const value = raw == null ? '' : String(raw).trim();
  if (!value) return '';

  const digits = value.replace(/\D/g, '');
  const hasCountryCode = digits.length === 11 && digits.startsWith('1');
  // A '+' number is only North American with the +1 country code.
  if (value.startsWith('+') && !hasCountryCode) return value;

  const national = hasCountryCode ? digits.slice(1) : digits;
  if (national.length !== 10) return value;
  return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
}

/** `formatPhone`, with '-' standing in for an empty value. */
export function formatPhoneNumber(
  raw: string | number | null | undefined
): string {
  return formatPhone(raw) || '-';
}

/**
 * A `tel:` href for a phone number: its digits, keeping a leading '+' so an
 * international number still dials.
 */
export function phoneHref(raw: string | number): string {
  const value = String(raw).trim();
  const digits = value.replace(/\D/g, '');
  return `tel:${value.startsWith('+') ? '+' : ''}${digits}`;
}
